import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { event_status } from '../../../../../assets/constData/event_status';
import { CommonServiceService } from '../../../../utilities/services/common-service/common-service.service';
import { OrderHistoryService } from '../../../../utilities/services/order-history/order-history.service';
import { eventImageUrl, hasImageUrlFallback } from '../../../../utilities/event-image-url.util';

/** Status title that unlocks the "Register now" call to action. */
const REGISTRATION_OPEN = 'REGISTRATION OPEN';

/** Route the login flow returns to after a registration-gated sign-in. */
const REDIRECT_AFTER_LOGIN = '/events/1';

/** Visual state driving the status pill's dot colour and the card's accent bar. */
export type Template1EventVisualStatus =
  | 'ongoing'
  | 'upcoming'
  | 'recent'
  | 'completed';

/**
 * Maps a real `event_status` value onto one of the four visual states the
 * reference design paints. Decoration only — the pill always prints the real
 * translated status title, so an unmapped status still reads correctly.
 */
export const VISUAL_STATUS_BY_VALUE: Readonly<Record<number, Template1EventVisualStatus>> = {
  1: 'upcoming', // REGISTRATION OPEN
  2: 'upcoming', // REGISTRATION CLOSED
  3: 'upcoming', // DRAWS IN MAKING
  4: 'ongoing', // DRAWS OUT
  8: 'upcoming', // PENDING
  13: 'ongoing', // ONGOING
  14: 'upcoming', // UPCOMING
  15: 'recent', // RECENT
};

/** Countdown returned by `get_events` describing when registration shuts. */
export interface Template1RegistrationCloses {
  readonly total_days?: number;
  readonly total_hours?: number;
}

/**
 * The subset of an events-list row this card renders.
 *
 * Declared structurally (not as a full API model) so the parent's existing
 * loosely-typed `events_list` assigns without a cast, while the card itself
 * stays free of `any`.
 */
export interface Template1EventItem {
  readonly id: number;
  readonly name: string;
  readonly event_type?: string;
  readonly event_status?: number | string;
  readonly event_start_date?: string;
  readonly event_end_date?: string;
  readonly banner?: string;
  readonly thumbnail?: string;
  readonly registered_participants?: number;
  readonly registration_closes_in?: Template1RegistrationCloses | null;
  readonly delegation?: string;
}

/**
 * Template-1 (OTT) poster card for one row of the events listing.
 *
 * Presentational only: it owns no data fetching and no routing. The
 * register/view decision is ported verbatim from `TournamentCardComponent` so
 * both templates route a click to exactly the same parent handler, and the
 * parent binds the identical output set it already binds on the template-0 card.
 */
@Component({
  selector: 'stupa-template1-event-card',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './template1-event-card.component.html',
  styleUrl: './template1-event-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Template1EventCardComponent {
  /** Event row to render. */
  @Input({ required: true }) item!: Template1EventItem;

  /** Venue address resolved by the parent from the row's `extra_json`. */
  @Input() location = '';

  /** Broadcast listings have no registration count to link to. */
  @Input() isBroadcastRoute = false;

  /** User asked to register for this event (old flow). */
  @Output() readonly register = new EventEmitter<void>();

  /** User clicked Register Now on banner hover (new flow). */
  @Output() readonly newRegister = new EventEmitter<void>();

  onNewRegisterClick(event: Event): void {
    event.stopPropagation();
    this.saveEventDataInSession();
    this.newRegister.emit();
  }

  /** Card body was clicked — open the event detail screen. */
  @Output() readonly cardClick = new EventEmitter<void>();

  /** Registration is not open, so the CTA opens the detail screen instead. */
  @Output() readonly viewDetails = new EventEmitter<void>();

  /** A signed-out user hit a registration-gated action. */
  @Output() readonly showLogin = new EventEmitter<void>();

  /** The registered-participants figure was clicked. */
  @Output() readonly viewRegisterCount = new EventEmitter<void>();

  constructor(
    private readonly commonService: CommonServiceService,
    private readonly orderService: OrderHistoryService,
  ) {}

  /** Poster image, preferring the broadcast thumbnail like the template-0 card. */
  get image(): string {
    const raw = this.item?.thumbnail || this.item?.banner;
    return eventImageUrl(raw, !!raw && raw === this.failedImage);
  }

  /** Raw URL whose rewritten form failed to load; the card then retries the original URL. */
  private failedImage: string | null = null;

  onImageError(): void {
    const raw = this.item?.thumbnail || this.item?.banner;
    if (raw !== this.failedImage && hasImageUrlFallback(raw)) {
      this.failedImage = raw ?? null;
    }
  }

  /** Real status title from `event_status`, used as the pill's translate key. */
  get statusTitle(): string {
    const match = event_status.find(
      (entry) => Number(entry.value) === Number(this.item?.event_status),
    );
    return match ? match.title : '';
  }

  /** Visual state for the pill dot and top accent bar. */
  get visualStatus(): Template1EventVisualStatus {
    return VISUAL_STATUS_BY_VALUE[Number(this.item?.event_status)] ?? 'completed';
  }

  /** True while the event still accepts registrations. */
  get isRegistrationOpen(): boolean {
    return this.statusTitle === REGISTRATION_OPEN;
  }

  /** Translate key for the primary action, matching the template-0 card. */
  get ctaKey(): string {
    return this.isRegistrationOpen ? 'register_now' : 'view_event_details';
  }

  /** Translate key naming the event kind; leagues carry their own accent. */
  get typeKey(): string {
    return this.isLeague ? 'leagues' : 'tournaments';
  }

  /** Leagues are `L` (or `BL` on the broadcast listing). */
  get isLeague(): boolean {
    return (this.item?.event_type ?? '').toUpperCase().endsWith('L');
  }

  /**
   * Days/hours left to register, or `null` when that badge should not show.
   *
   * Ported from `TournamentCardComponent.getClosesIn` so both templates hide
   * the badge under identical conditions (no countdown, or it has run out).
   */
  get closesIn(): Template1RegistrationCloses | null {
    const closes = this.item?.registration_closes_in;
    if (!closes || !this.isRegistrationOpen) {
      return null;
    }
    // A finished countdown reports zero/negative hours — nothing left to show.
    if (closes.total_hours !== undefined && closes.total_hours <= 0) {
      return null;
    }
    if (!closes.total_days) {
      return closes.total_hours && closes.total_hours > 0 ? closes : null;
    }
    return closes.total_days > 0 ? closes : null;
  }

  /** Whole number shown in the registration countdown chip. */
  get closesInCount(): number {
    const closes = this.closesIn;
    return closes?.total_days || closes?.total_hours || 0;
  }

  /**
   * Translate key for the countdown chip.
   *
   * Selected here rather than with a template ternary so the singular and
   * plural wordings stay separate keys each locale can translate on its own
   * plural rules.
   */
  get closesInKey(): string {
    const closes = this.closesIn;
    if (!closes) {
      return '';
    }
    const isDays = Boolean(closes.total_days);
    if (this.closesInCount === 1) {
      return isDays
        ? 'template1_events_closes_in_day'
        : 'template1_events_closes_in_hour';
    }
    return isDays
      ? 'template1_events_closes_in_days'
      : 'template1_events_closes_in_hours';
  }

  /**
   * Routes the primary action exactly as the template-0 card does.
   *
   * Ported verbatim from `TournamentCardComponent.onRegister` — the three
   * branches (sign-in gate, closed-registration fallthrough, register) must
   * stay identical so switching template never changes where a click lands.
   */
  onCtaClick(event: Event): void {
    event.stopPropagation();

    const token = globalThis.window?.localStorage
      ? localStorage.getItem('token')
      : null;

    if (!token && this.isRegistrationOpen) {
      this.saveEventDataInSession();
      this.commonService.login_subject.next(true);
      this.commonService.is_login_success.next(false);
      this.showLogin.emit();
      return;
    }

    if (!this.isRegistrationOpen) {
      this.viewDetails.emit();
      return;
    }

    this.register.emit();
  }

  /** Opens the event detail screen from the card surface. */
  onCardClick(): void {
    this.cardClick.emit();
  }

  /** Opens the participants view for this event. */
  onViewRegisterCount(event: Event): void {
    event.stopPropagation();
    this.viewRegisterCount.emit();
  }

  /**
   * Primes session storage so the post-login redirect can resume registration.
   *
   * Ported from `TournamentCardComponent.saveEventDataInSession`; the keys are
   * read by the registration flow and must not be renamed here alone.
   */
  private saveEventDataInSession(): void {
    if (!globalThis.window?.sessionStorage) {
      return;
    }
    const event = this.item;
    sessionStorage.setItem('selectedTournamentName', event.name);
    sessionStorage.setItem('selectedTournamentDetails', JSON.stringify(event));
    sessionStorage.setItem('event_id', String(event.id));
    sessionStorage.setItem('event_type', event.event_type ?? '');
    sessionStorage.setItem('delegation', event.delegation ?? '');
    sessionStorage.setItem('event', JSON.stringify(event));
    sessionStorage.setItem('redirectAfterLogin', REDIRECT_AFTER_LOGIN);
    this.orderService.selectedTournamentName = event.name;
  }
}
